import { useCallback, useEffect, useRef, useState } from 'react';
import {
  CACHE_VERSION_POLL_INTERVAL_MS,
  CACHED_APP_VERSION,
  fetchProductionVersion,
  isVersionMismatch,
} from '../utils/cacheVersion';
import { usePageVisible } from './display/usePageVisible';

export interface CacheVersionCheckResult {
  cachedVersion: string;
  latestVersion: string | null;
  hasUpdate: boolean;
}

export function useCacheVersionCheck(
  intervalMs: number = CACHE_VERSION_POLL_INTERVAL_MS,
): CacheVersionCheckResult {
  const [latestVersion, setLatestVersion] = useState<string | null>(null);
  const isPageVisible = usePageVisible();
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const check = useCallback(async () => {
    const version = await fetchProductionVersion();
    if (version) setLatestVersion(version);
  }, []);

  useEffect(() => {
    if (!isPageVisible) return;

    check();
    intervalRef.current = setInterval(check, intervalMs);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [check, intervalMs, isPageVisible]);

  return {
    cachedVersion: CACHED_APP_VERSION,
    latestVersion,
    hasUpdate: isVersionMismatch(CACHED_APP_VERSION, latestVersion),
  };
}
